import { ItemEngineStateClientChangeFeed } from "@hds-products/event-runtime-entities-client";
import { useItemEngineStateStore } from '../pinia/itemEngineStateStore';
import { useAdminSdkStatusStore } from '../pinia/adminSdkStatusStore';
import { compare } from 'fast-json-patch';

class ItemEngineStatePiniaVueAdapter {
    #_changeFeed;
    #_deltaServerClient;
    #_itemEngineStatePiniaStore;
    #_sdkStatusPiniaStore;
    #_eventDetailIdToWatcherIdMap = new Map();
    #_itemsByIdWatcher = new Map();
    constructor(deltaServerClient, itemEngineStateBaseUri, customHeaderFn) {
        this.#_deltaServerClient = deltaServerClient;
        this.#_changeFeed = new ItemEngineStateClientChangeFeed(deltaServerClient, itemEngineStateBaseUri, customHeaderFn, null);
        this.#_itemEngineStatePiniaStore = useItemEngineStateStore();
        this.#_sdkStatusPiniaStore = useAdminSdkStatusStore();
        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('item-engine-state', { title: 'Item Engine', description: 'Engine processing state for items', status: 'INITIALIZING' })
        this.onAdded = this.onAdded.bind(this);
        this.onUpdated = this.onUpdated.bind(this);
        this.onRemoved = this.onRemoved.bind(this);
    }
    async _connectAndStartChangeFeed(isRestart = false) {
        let attempts = 1;
        if (this.#_deltaServerClient.isConnected()) {
            await this.#_changeFeed.whenStarted();
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('item-engine-state', { status: 'CONNECTED', attempts })
        } else {
            while (!this.#_deltaServerClient.isConnected()) {
                try {
                    await this.#_deltaServerClient.whenConnected();
                    if (attempts === 1) {
                        console.info(`item-engine-state changefeed connected`);
                    } else {
                        console.info(`item-engine-state changefeed connected after attempt #${attempts}.`);
                    }
                    if (isRestart) {
                        await this.#_changeFeed.restart();
                    } else {
                        await this.#_changeFeed.whenStarted();
                    }
                    attempts = 1;
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('item-engine-state', { status: 'CONNECTED', attempts })
                } catch (err) {
                    //A refused principal stays refused, so another attempt cannot succeed
                    if (err?.closeCode === 4403) {
                        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('item-engine-state', { status: 'DISCONNECTED', attempts })
                        return;
                    }
                    attempts++;
                    console.error(`Attempt #${attempts}: item-engine-state changefeed error: ${err}`);
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('item-engine-state', { status: 'DISCONNECTED', attempts })
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
        }
    }
    async initialize() {
        this.#_changeFeed.addFailureListener(async (error) => {
            console.error(`Change Feed Failure (ItemEngineStateChangeFeed) => ${error.message}`);
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('item-engine-state', { status: 'DISCONNECTED' })
            await this._connectAndStartChangeFeed(true);
        }, this);
        await this._connectAndStartChangeFeed();
    }
    async watchItemsByEventDetailId(eventDetailId) {
        const watcher = await this.#_changeFeed.watchByProperties({ eventDetailId });
        watcher.on('entity-added', this.onAdded)
        watcher.on('entity-updated', this.onUpdated)
        watcher.on('entity-removed', this.onRemoved)
        this.#_eventDetailIdToWatcherIdMap.set(eventDetailId, watcher.watchId);
        watcher.entities?.forEach((value, key) => {
            this.onAdded(value);
        })
    }
    async unwatchItemsByEventDetailId(eventDetailId) {
        const watcherId = this.#_eventDetailIdToWatcherIdMap.get(eventDetailId);
        const watcher = this.#_changeFeed.getWatcher(watcherId);
        watcher.off('entity-added', this.onAdded);
        watcher.off('entity-updated', this.onUpdated);
        watcher.off('entity-removed', this.onRemoved);
        watcher.entities?.forEach((value, key) => {
            this.onRemoved(value.id);
        })
        this.#_changeFeed.unwatch(watcherId);
        this.#_eventDetailIdToWatcherIdMap.delete(eventDetailId);
    }
    async watchItemsByItemId(ids = []) {
        ids.forEach(async (id) => {
            if (this.#_itemsByIdWatcher.has(id)) {
                return;
            } else {
                const watcher = await this.#_changeFeed.watchById(id);
                watcher.on('entity-added', this.onAdded);
                watcher.on('entity-updated', this.onUpdated);
                watcher.on('entity-removed', this.onRemoved);
                this.#_itemsByIdWatcher.set(id, watcher);
                watcher.entities?.forEach((value, key) => {
                    this.onAdded(value);
                })
            }
        })
    }
    async unwatchItemsByItemId(ids = []) {
        ids.forEach(async (id) => {
            if (!this.#_itemsByIdWatcher.has(id)) { return; }
            const watcher = this.#_itemsByIdWatcher.get(id);
            watcher.off('entity-added', this.onAdded);
            watcher.off('entity-updated', this.onUpdated);
            watcher.off('entity-removed', this.onRemoved);
            watcher.entities?.forEach((value, key) => {
                this.onRemoved(value.id);
            })
            await this.#_changeFeed.unwatch(watcher.watchId);
            this.#_itemsByIdWatcher.delete(id);
        })
    }
    async watchItem(id) {
        if (!id || this.#_itemsByIdWatcher.has(id)) {
            return;
        } else {
            const watcher = await this.#_changeFeed.watch(id);
            watcher.on('entity-added', this.onAdded);
            watcher.on('entity-updated', this.onUpdated);
            watcher.on('entity-removed', this.onRemoved);
            this.#_itemsByIdWatcher.set(id, watcher);
            watcher.entities?.forEach((value, key) => {
                this.onAdded(value);
            })
        }
    }
    async unwatchItem(id) {
        if (!id || !this.#_itemsByIdWatcher.has(id)) { 
            return; 
        } else {
            const watcher = this.#_itemsByIdWatcher.get(id);
            watcher.off('entity-added', this.onAdded);
            watcher.off('entity-updated', this.onUpdated);
            watcher.off('entity-removed', this.onRemoved);
            watcher.entities?.forEach((value, key) => {
                this.onRemoved(value.id);
            })
            await this.#_changeFeed.unwatch(watcher.watchId);
            this.#_itemsByIdWatcher.delete(id);
        }
    }
    onAdded(itemEngineStateEntity) {
        this.#_itemEngineStatePiniaStore.addItemEngineState(itemEngineStateEntity.toJSON());
    }
    onUpdated(itemEngineStateId, preModel, newEntity) {
        const jsonPatch = compare(preModel, newEntity.toJSON());
        if (jsonPatch.length > 0) {
            this.#_itemEngineStatePiniaStore.applyJsonPatch(itemEngineStateId, jsonPatch);
        }
    }
    onRemoved(itemEngineStateId) {
        this.#_itemEngineStatePiniaStore.removeItemEngineState(itemEngineStateId);
    }
}

export { ItemEngineStatePiniaVueAdapter }
export default ItemEngineStatePiniaVueAdapter;